import React from 'react';
import classnames from 'classnames';
import { Field, Form, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import FontAwesomeIcon from 'react-fontawesome';
import { RouteComponentProps } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { TextField } from '@mui/material';
import { ValidationErrors, FormApi } from 'final-form';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import Sidebar from './Sidebar';
import { required, STORE_CLOSING_NS } from './Constants';
import {
    deleteClosure,
    enqueueNotification,
    saveUpdateClosure
} from './actions/Actions';
import { Closure, Store } from './types';
import DeleteButton from './DeleteButton';

type FormValues = Partial<Closure>;

const renderTextInput = params => (
    <TextField {...params} className='date-picker field-input' />
);

const FormDateTimePicker = ({ input, shouldDisableDate }) => (
    <DatePicker
        {...input}
        inputFormat='DD.MM.yyyy'
        shouldDisableDate={shouldDisableDate}
        showTodayButton
        disableMaskedInput
        // eslint-disable-next-line react/jsx-no-bind
        renderInput={renderTextInput}
    />
);

const StoreClosing: React.FC<RouteComponentProps> = () => {
    const { t } = useTranslation();
    const dispatch: ThunkDispatch<Store, unknown, AnyAction> = useDispatch();
    const formRef = React.useRef<FormApi<FormValues>>();

    const [loading, setLoading] = React.useState(false);
    const [selectedClosure, setSelectedClosure] = React.useState<FormValues>(null);
    const closures = useSelector(({ app }: Store) => app.closures);

    let formValues: FormValues = { ...selectedClosure };

    if (!selectedClosure) {
        formValues = {
            closureNotification: '',
            shortDescription: '',
            effectiveByDate: moment(),
            startDate: moment(),
            endDate: moment().add(1, 'day')
        };
    }

    const validateForm = React.useCallback(
        (values: FormValues): ValidationErrors => {
            const errors: ValidationErrors = {};

            if (!values.closureNotification) {
                errors.closureNotification = t('required_field');
            }

            if (!values.shortDescription) {
                errors.shortDescription = t('required_field');
            }

            if (!values.shortDescription) {
                errors.shortDescription = t('required_field');
            }

            if (values.startDate.isAfter(values.endDate, 'D')) {
                errors.startDate = t(`${STORE_CLOSING_NS}.form.invalid_range`);
            }

            if (values.effectiveByDate.isAfter(values.startDate, 'D')) {
                errors.effectiveByDate = t(
                    `${STORE_CLOSING_NS}.form.invalid_display_date`
                );
            }

            return errors;
        },
        [t]
    );

    const updateSelectedClosure = React.useCallback(
        (closure: Closure) => () => {
            setSelectedClosure(closure);
        },
        []
    );

    const resetStateAndForm = React.useCallback(() => {
        setSelectedClosure(null);

        formRef.current.reset();

        (
            [
                'closureNotification',
                'shortDescription',
                'effectiveByDate',
                'startDate',
                'endDate'
            ] as const
        ).forEach(field => {
            formRef.current.resetFieldState(field);
        });
    }, []);

    const handleFormSubmit = React.useCallback(
        (values: FormValues) => {
            setLoading(true);

            dispatch(saveUpdateClosure(values))
                .then(() => {
                    resetStateAndForm();
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [dispatch, resetStateAndForm]
    );

    const shouldDisableDate = React.useCallback(
        (date: moment.Moment): boolean => {
            if (!selectedClosure) {
                return date.isBefore(moment(), 'D');
            }

            const { effectiveByDate, endDate, startDate } = selectedClosure;
            const minDate = moment.min(effectiveByDate, endDate, startDate, moment());

            return date.isBefore(minDate, 'D');
        },
        [selectedClosure]
    );

    const handleClosureDeletion = React.useCallback(
        (id: number) => () => {
            setLoading(true);

            dispatch(deleteClosure(id))
                .then(() => {
                    resetStateAndForm();
                })
                .catch((e: Error) => {
                    dispatch(
                        enqueueNotification({
                            key: new Date().getTime(),
                            message: e.message,
                            options: { variant: 'error' }
                        })
                    );
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        [dispatch, resetStateAndForm]
    );

    const renderForm = React.useCallback(
        ({
            handleSubmit,
            submitting,
            hasValidationErrors,
            form,
            values
        }: FormRenderProps<FormValues>) => {
            const disabled = submitting || hasValidationErrors;
            formRef.current = form;

            return (
                <form className='form' onSubmit={handleSubmit}>
                    <label className='form-field'>
                        <label className='wide'>{t(`${STORE_CLOSING_NS}.from`)}:</label>
                        <Field name='startDate'>
                            {({ input, meta }) => (
                                <div className='field-input date-picker'>
                                    <FormDateTimePicker
                                        input={input}
                                        shouldDisableDate={shouldDisableDate}
                                    />
                                    {meta.error && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='wide'>{t(`${STORE_CLOSING_NS}.to`)}:</label>
                        <Field name='endDate'>
                            {({ input }) => (
                                <div className='field-input date-picker'>
                                    <FormDateTimePicker
                                        input={input}
                                        shouldDisableDate={shouldDisableDate}
                                    />
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${STORE_CLOSING_NS}.message.label`)}:
                        </label>
                        <Field
                            name='closureNotification'
                            validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <textarea {...input} rows={6} cols={30} />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${STORE_CLOSING_NS}.description.label`)}:
                        </label>
                        <Field
                            name='shortDescription'
                            validate={required(t('required_field'))}>
                            {({ input, meta }) => (
                                <div className='field-input'>
                                    <input {...input} />
                                    {meta.error && meta.touched && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <label className='form-field'>
                        <label className='wide'>
                            {t(`${STORE_CLOSING_NS}.message.displayFrom`)}:
                        </label>
                        <Field name='effectiveByDate'>
                            {({ input, meta }) => (
                                <div className='field-input date-picker'>
                                    <FormDateTimePicker
                                        input={input}
                                        shouldDisableDate={shouldDisableDate}
                                    />
                                    {meta.error && (
                                        <FontAwesomeIcon
                                            className='error-icon'
                                            name='exclamation-circle'
                                            title={meta.error}
                                        />
                                    )}
                                </div>
                            )}
                        </Field>
                    </label>
                    <div className='action-buttons'>
                        <button className='save' type='submit' disabled={disabled}>
                            {t('save')}
                        </button>
                        <DeleteButton
                            disabled={disabled || values.id == null}
                            title={t(`${STORE_CLOSING_NS}.delete_closure`)}
                            message={t(
                                `${STORE_CLOSING_NS}.delete_closure_confirm`,
                                values
                            )}
                            onAccept={handleClosureDeletion(values.id)}
                        />
                        <div style={{ alignSelf: 'center' }}>
                            {loading && (
                                <FontAwesomeIcon name='spinner' className='fa-spin' />
                            )}
                        </div>
                    </div>
                </form>
            );
        },
        [t, handleClosureDeletion, loading, shouldDisableDate]
    );

    return (
        <div className='admin-form main-content store-closing'>
            <Sidebar>
                <FontAwesomeIcon
                    name='plus'
                    className='add-new'
                    onClick={resetStateAndForm}
                />
                <div className='content'>
                    {closures.map(cl => (
                        <div
                            key={cl.id}
                            className={classnames('item', {
                                selected: cl.id === selectedClosure?.id
                            })}
                            onClick={updateSelectedClosure(cl)}>
                            {cl.shortDescription}
                        </div>
                    ))}
                </div>
            </Sidebar>
            <div className='store-closing-form'>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Form<FormValues>
                        initialValues={{ ...formValues }}
                        render={renderForm}
                        onSubmit={handleFormSubmit}
                        validate={validateForm}
                    />
                </LocalizationProvider>
            </div>
        </div>
    );
};

export default StoreClosing;
